import {
  HttpClient,
  HttpErrorResponse,
  HttpStatusCode,
} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { filter, firstValueFrom } from "rxjs";
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
  CanActivateFn,
} from "@angular/router";
import { engagementAllEngagement } from "backend/api/engagement.service";
import { AllEngagementQueryType } from "backend/model/allEngagementQueryType";
import { EngagementState } from "backend/model/engagementState";
import { GetUserContextQueryResult } from "backend/model/getUserContextQueryResult";
import { TeamMemberStatus } from "backend/model/teamMemberStatus";
import { IS_CLASSIC_ENGAGEMENT_KEY } from "src/app/classic/classic-interceptor";
import { getEffectiveAptUrl } from "src/app/guards/getEffectiveAptUrl";
import { APTContext } from "src/app/services/aptContext/apt-context";
import { RemoteStorageStates } from "src/app/services/RemoteStorageStates";
import { WorkspaceListNavigationService } from "src/app/services/workspace-list-navigation.service";
import {
  CROSSBORDER_BLOCKLIST_WARNING,
  PROBLEM_DETECTED,
  REMOVED_WORKSPACE_WARNING,
  STALE_REMOTE_STORAGE_WARNING,
} from "src/app/services/workspace-list-navigation.service";
import { TEAM_MANAGEMENT_PATH } from "src/app/workspace/routes";
import { StandardConfirmationDialogRequest } from "src/standard/standard-confirmation-dialog/standard-confirmation-dialog.types";
import { ModalDialogService } from "src/standard/standard-modal-button/showModalDialog";
import { AptUrlInfo, parseAptUrl } from "src/standard/ts/parseAptUrl";
import invariant from "tiny-invariant";
import { EngagementDetails } from "backend/model/engagementDetails";

@Injectable({
  providedIn: "root",
})
export class WorkspaceGuardService {
  private classicEngagementIds: string[] | undefined;
  private userContext: GetUserContextQueryResult | undefined;
  private currentEngagement: EngagementDetails | undefined;

  private aptContext = inject(APTContext);
  private router = inject(Router);
  private httpClient = inject(HttpClient);
  private workspaceListNavigationService = inject(
    WorkspaceListNavigationService,
  );
  private modalDialogService = inject(ModalDialogService);

  async handleRedirectsBasedOnEngagementDetails(
    engagementId: string,
  ): Promise<void> {
    try {
      // Refresh engagement details
      this.currentEngagement = await this.getEngagementDetails(engagementId);
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        switch (error.status) {
          // Likely for security reasons, it throws
          // a 400 error instead of Unauthorized here
          case HttpStatusCode.BadRequest:
          case HttpStatusCode.Unauthorized: {
            await this.modalDialogService.showConfirmationDialog(
              {
                variant: "informational",
                message: $localize`You do not have the appropriate permissions to access this engagement. If you still require access, contact a team admin. This workspace will close when you click OK.`,
              },
              {
                closeOnNavigation: false,
              },
            );
            await this.router.navigate([`/ws/`]);
          }
        }
      }

      // if its not an HttpErrorResponse, then we assume we popped a dialog with the error from the aptgetresult method.
      // and therefore should cancel the navigation to the engagment.
      await this.router.navigate([`/ws/`]);
      return;
    }

    // Redirect to home if engagement is in remote storage
    if (RemoteStorageStates.includes(this.currentEngagement.state)) {
      await this.showWarningAndReturnToWorkspaceListing(
        STALE_REMOTE_STORAGE_WARNING,
      );
      return;
    }

    // Redirect to home if engagement is cross-border blocklist
    if (this.currentEngagement.isCrossBorderBlockListEngagement) {
      await this.showWarningAndReturnToWorkspaceListing(
        CROSSBORDER_BLOCKLIST_WARNING,
      );
      return;
    }

    // Redirect to home if user is not active or invited
    let usr;
    try {
      usr = await this.getCurrentUserDetails(engagementId);
      if (
        !this.userContext?.isSupportUser &&
        usr?.status !== TeamMemberStatus.Active &&
        usr?.status !== TeamMemberStatus.Invited
      ) {
        await this.showWarningAndReturnToWorkspaceListing(
          REMOVED_WORKSPACE_WARNING,
        );
        return;
      }
    } catch {
      await this.showWarningAndReturnToWorkspaceListing(
        REMOVED_WORKSPACE_WARNING,
      );
      return;
    }

    if (!this.userContext?.isSupportUser) {
      // Do not wait for the update to complete
      void this.updateTeamMemberStatusAndRefetchCurrentUser(
        engagementId,
        usr?.memberFirmId ?? this.currentEngagement.memberFirmId,
        usr.status === TeamMemberStatus.Invited,
      );
    }
  }

  async canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    const [url] = getEffectiveAptUrl(state.url);
    const aptUrlInfo = parseAptUrl(url);

    this.aptContext.isUserViewingEngagement.set(
      aptUrlInfo.isWithinEngagement ?? false,
    );

    if (!aptUrlInfo.isWithinEngagement) {
      return true;
    }

    const engagementId = aptUrlInfo.engagementId;
    invariant(engagementId);
    // Bypass guard if navigating from workspace listing panel to avoid duplicating checks
    if (
      engagementId ===
      this.workspaceListNavigationService.lastEngagementIdNavigatedTo
    ) {
      void this.populateEngagementDetails(engagementId);
      return true;
    }

    // Only perform checks when navigating to a new engagement
    if (
      this.currentEngagement === undefined ||
      this.currentEngagement.engagementId !== engagementId
    ) {
      // Get user context and populate classic engagement data
      const getUserContextAndPopulateClassicEngagementData = async () => {
        const userContext = this.userContext ?? (await this.getUserContext());
        // Set isClassicEngagement flag for classic interceptor
        if (userContext.isClassicEngagementSupported) {
          await this.populateClassicEngagementData(engagementId);
        }

        return userContext;
      };

      const userContext =
        await getUserContextAndPopulateClassicEngagementData();
      this.userContext = userContext;

      await this.handleRedirectsBasedOnEngagementDetails(engagementId);

      if (this.userContext.isSupportUser) {
        // Only allow support user to access team management page
        const isOnTeamManagementPage = this.isOnTeamManagementPage(
          aptUrlInfo,
          TEAM_MANAGEMENT_PATH,
        );

        if (isOnTeamManagementPage) {
          return true;
        }

        void this.router.navigate(["ws", engagementId, TEAM_MANAGEMENT_PATH]);
        return false;
      }

      return true;
    }

    return true;
  }

  isOnTeamManagementPage(aptUrlInfo: AptUrlInfo, teamManagementPath: string) {
    const parts = aptUrlInfo.urlParts;

    if (!parts.length || !teamManagementPath) {
      return false;
    }

    const target = teamManagementPath.toLowerCase();
    const teamManagementIndex = parts.findIndex(
      (p) => p.toLowerCase() === target,
    );
    const onTeamManagementPage = teamManagementIndex > -1;

    return onTeamManagementPage;
  }

  async populateClassicEngagementData(engagementId: string) {
    if (this.classicEngagementIds === undefined) {
      try {
        this.classicEngagementIds = await this.getClassicEngagementIds();
      } catch {
        this.classicEngagementIds = [];
      }
    }

    sessionStorage.setItem(
      IS_CLASSIC_ENGAGEMENT_KEY,
      this.classicEngagementIds.includes(engagementId).toString(),
    );
  }

  async getUserContext(): Promise<GetUserContextQueryResult> {
    return firstValueFrom(
      this.aptContext.userContext$.pipe(
        filter((x): x is GetUserContextQueryResult => !!x),
      ),
    );
  }

  async getClassicEngagementIds(): Promise<string[]> {
    const result = await this.aptContext.getAPTResultOrThrow(
      await engagementAllEngagement(this.httpClient, {
        request: {
          queryType: AllEngagementQueryType.FullQuery,
          isClassic: true,
        },
      }),
      "toast",
      false,
    );

    return result.map((e) => e.engagementId).filter((id) => id !== undefined);
  }

  public async getEngagementDetails(engagementId: string) {
    const details = await this.aptContext.fetchEngagementDetails(engagementId);
    return await this.aptContext.getAPTResultOrThrow(details, "dialog", true);
  }

  public async getCurrentUserDetails(engagementId: string) {
    const usr = await this.aptContext.fetchWhoAmI(engagementId);
    return await this.aptContext.getAPTResultOrThrow(usr, "dialog", true);
  }

  async populateEngagementDetails(engagementId: string) {
    if (
      this.currentEngagement === undefined ||
      this.currentEngagement.engagementId !== engagementId
    ) {
      this.currentEngagement = await this.getEngagementDetails(engagementId);
    }
  }

  async showWarningAndReturnToWorkspaceListing(message: string) {
    const modalConfig = {
      title: PROBLEM_DETECTED,
      message,
      variant: "informational",
    } satisfies StandardConfirmationDialogRequest;

    await this.modalDialogService.showConfirmationDialog(modalConfig);
    sessionStorage.removeItem(IS_CLASSIC_ENGAGEMENT_KEY);
    void this.router.navigate(["ws"]);
  }

  async updateTeamMemberStatusAndRefetchCurrentUser(
    engagementId: string,
    memberFirmId: number,
    isInvitedStatus: boolean,
  ) {
    await this.workspaceListNavigationService.updateTeamMemberStatus(
      engagementId,
      EngagementState.Active,
      memberFirmId,
    );

    if (isInvitedStatus) {
      await this.aptContext.refetchWhoAmIWithoutThrottle();
    }
  }
}

export const WorkspaceGuard = (async (
  next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const workspaceGuardService = inject(WorkspaceGuardService);
  return workspaceGuardService.canActivate(next, state);
}) satisfies CanActivateFn;
